import RefData from 'ref-data';

import { ajaxService } from '../../common/Services';
import { ApiUtils } from '../../common/api';
import IntegrationCollection from './integration-collection';
import IntegrationModel from './integration-model';
import IntegrationTemplateCollection from './integration-template-collection';
import IntegrationCredentialsModel from './integration-credentials-model';
import IntegrationCredentialsCollection from './integration-credentials-collection';

/**
 * fetch - return a list of integrations
 * @param {string} containerRef the container ref required to filter the integration list
 * @param {string} classRef filter the list by classRef
 */
function fetch({ containerRef, classRef, typeRef }) {
  return ajaxService
    .post('/api/integration/list.json', {
      containerRef,
      classRef,
      order: 'asc',
      sort:  'dateCreated',
      typeRef,
    })
    .then(ApiUtils.validate, ApiUtils.validate)
    .then(ApiUtils.extract)
    .then(IntegrationCollection.deserialize);
}

/**
 * fetchTemplates - return the list of integration templates
 * @param {string} containerRef the container ref required to filter the template list
 * @param {string} classRef filter the templates by classRef
 * @param {string} validFor filter the templates by experience type (campaign level only)
 */
function fetchTemplates({ containerRef, classRef, validFor }) {
  //used to get the list of templates for the picker
  return ajaxService
    .get('/api/integrationtemplate/list.json', {
      containerRef,
      classRef,
      status:                    RefData.Enum.STATUS.INTEGRATIONTEMPLATE.ACTIVE,
      order:                     'desc',
      sort:                      'name',
      validForFormatType_filter: validFor,
    })
    .then(ApiUtils.validate, ApiUtils.validate)
    .then(ApiUtils.extract)
    .then(IntegrationTemplateCollection.deserialize)
    .catch((error) => ({ error }));
}

/**
 * createFromTemplate - create a new integration from an integration template
 * @param {string} templateId the template from which you will create your integration
 * @param {string} containerRef the container you wich to attach your integration to
 * @param {object} context additional non-wizard fields, like credentialsId
 * @param {object} wizardFields additional fields, applied only for wizard integrations
 */
function createFromTemplate({ templateId, containerRef, context, wizardFields }) {
  const params = {
    templateId,
    containerRef,
    ...(context && { ...context }),
    ...(wizardFields && {
      blueprintData: JSON.stringify(wizardFields),
      name:          wizardFields.name,
    }),
  };
  return ajaxService
    .post('/api/integration/createFromTemplate.json', params)
    .then(ApiUtils.validate, ApiUtils.validate)
    .then(ApiUtils.extract1)
    .then(IntegrationModel.deserialize);
}

/**
 * enableIntegration
 * @param {string|number} integrationId the id of the integration to be enabled
 */

function enableIntegration({ integrationId }) {
  return ajaxService
    .get('/api/integration/enable.json', {
      id: integrationId,
    })
    .then(ApiUtils.validate, ApiUtils.validate)
    .then(ApiUtils.extract1)
    .then(IntegrationModel.deserialize);
}

/**
 * disableIntegration
 * @param {string|number} integrationId the id of the integration to be disabled
 */

function disableIntegration({ integrationId }) {
  return ajaxService
    .get('/api/integration/disable.json', {
      id: integrationId,
    })
    .then(ApiUtils.validate, ApiUtils.validate)
    .then(ApiUtils.extract1)
    .then(IntegrationModel.deserialize);
}

/**
 * deleteIntegration
 * @param {string|number} integrationId the id of the integration to be deleted
 */
function deleteIntegration({ integrationId }) {
  return ajaxService
    .post('/api/integration/delete.json', {
      id: integrationId,
    })
    .then(ApiUtils.validate, ApiUtils.validate)
    .then(ApiUtils.extract1);
}

/**
 * updateIntegration - update an integration instance
 * @param {object} integration the integration being updated
 * @param {string} status
 */
function updateIntegration({ integration, status }) {
  return ajaxService
    .post('/api/integration/update.json', {
      id:             integration.id,
      name:           integration.name,
      instanceConfig: IntegrationModel.serialize(integration).instanceConfig,
      status,
    })
    .then(ApiUtils.validate, ApiUtils.validate)
    .then(ApiUtils.extract1)
    .then(IntegrationModel.deserialize);
}

/**
 * patchIntegration - unlike update, this one ONLY partially updates the instanceConfig of an integration
 * @param {object} integration the integration being updated
 * @param {string} status
 */
function patchIntegration({ integrationId, instanceConfig }) {
  return ajaxService
    .post('/api/integration/patch.json', {
      id:             integrationId,
      instanceConfig: instanceConfig,
    })
    .then(ApiUtils.validate, ApiUtils.validate)
    .then(ApiUtils.extract1)
    .then(IntegrationModel.deserialize)
    .catch((error) => ({ error }));
}

/**
 * fetchCredentials - fetch the credentials list, used in the self serve integrations
 */
function fetchCredentials() {
  return ajaxService
    .get('/api/integrationapplication/list')
    .then(ApiUtils.validate, ApiUtils.validate)
    .then(ApiUtils.extract)
    .then(IntegrationCredentialsCollection.deserialize)
    .catch((error) => ({ error }));
}

function createCredentials({
  apiKey,
  appId,
  appSecret,
  consumerKey,
  consumerSecret,
  id,
  name,
  namespace,
  permissions,
  providerCopy,
  typeRef,
  url,
}) {
  return ajaxService
    .post('/api/integrationapplication/save', {
      apiKey,
      appId,
      appSecret,
      consumerKey,
      consumerSecret,
      id,
      name,
      namespace,
      permissions,
      providerCopy,
      status: RefData.Enum.STATUS.APPLICATION.ACTIVE,
      typeRef,
      url,
    })
    .then(ApiUtils.validate, ApiUtils.validate)
    .then(ApiUtils.extract1)
    .then(IntegrationCredentialsModel.deserialize)
    .catch((error) => ({ error }));
}

function updateCredentials({
  apiKey,
  appId,
  appSecret,
  consumerKey,
  consumerSecret,
  id,
  name,
  namespace,
  permissions,
  providerCopy,
  url,
}) {
  return ajaxService
    .post('/api/integrationapplication/update', {
      apiKey,
      appId,
      appSecret,
      consumerKey,
      consumerSecret,
      id,
      name,
      namespace,
      permissions,
      providerCopy,
      url,
    })
    .then(ApiUtils.validate, ApiUtils.validate)
    .then(ApiUtils.extract1)
    .then(IntegrationCredentialsModel.deserialize)
    .catch((error) => ({ error }));
}

function deleteCredentials({ credentialsId }) {
  return ajaxService
    .post('/api/integrationapplication/delete.json', {
      id: credentialsId,
    })
    .then(ApiUtils.validate, ApiUtils.validate)
    .then(ApiUtils.extract1)
    .catch((error) => ({ error }));
}

export default {
  fetch,
  fetchTemplates,
  createFromTemplate,
  enableIntegration,
  disableIntegration,
  deleteIntegration,
  updateIntegration,
  fetchCredentials,
  createCredentials,
  updateCredentials,
  deleteCredentials,
  patchIntegration,
};
